import type { TFunction } from 'i18next';

/**
 * WP1 "niczego nie blokujemy" (docs/decisions/2026-09-24-wp1-no-blocking.md):
 * a ZD or transport save goes through even when the stock valuation cannot be
 * recomputed. The save response says so — `valuationIncomplete` + reasons on a
 * ZD, a `STOCK_VALUATION_INCOMPLETE` entry in a transport's
 * `allocationWarnings` — and the page shows one Polish warning after saving.
 * Single home for reading both shapes and the sentence.
 */
export function readIncompleteValuationIssues(responseData: unknown): string[] | null {
  const record = responseData as {
    valuationIncomplete?: unknown;
    valuationIssues?: unknown;
    allocationWarnings?: unknown;
  } | null;
  if (!record) return null;
  const issues: string[] = [];
  let incomplete = record.valuationIncomplete === true;
  if (incomplete && Array.isArray(record.valuationIssues)) {
    issues.push(...record.valuationIssues.filter((issue): issue is string => typeof issue === 'string'));
  }
  if (Array.isArray(record.allocationWarnings)) {
    for (const warning of record.allocationWarnings) {
      const row = warning as { code?: unknown; issues?: unknown } | null;
      if (row?.code !== 'STOCK_VALUATION_INCOMPLETE') continue;
      incomplete = true;
      if (Array.isArray(row.issues)) {
        issues.push(...row.issues.filter((issue): issue is string => typeof issue === 'string'));
      }
    }
  }
  return incomplete ? issues.filter((issue, index) => issues.indexOf(issue) === index) : null;
}

export function incompleteValuationWarning(issues: string[], t: TFunction): string {
  return String(t('common:stockValuationIncompleteAfterSave', {
    reasons: issues.length > 0 ? issues.join(' ') : '',
  })).trim();
}

/**
 * Addendum A (decision 2026-09-30): a ZD or transport save re-splits the
 * freight of the ZD's other trucks after the commit; a failed re-split comes
 * back in `staleTransportIds`. One sentence for both pages, or null.
 */
export function staleTransportSplitWarning(responseData: unknown, t: TFunction): string | null {
  const ids = (responseData as { staleTransportIds?: unknown } | null)?.staleTransportIds;
  const count = Array.isArray(ids) ? ids.filter(Boolean).length : 0;
  return count > 0 ? String(t('common:staleTransportSplit', { count })) : null;
}

/**
 * Owner 2026-10-14: the ZD was cancelled but taking it off its trucks failed
 * after the save (`transportDetachFailed`, truck labels). One sentence, or null.
 */
export function transportDetachFailedWarning(responseData: unknown, t: TFunction): string | null {
  const labels = (responseData as { transportDetachFailed?: unknown } | null)?.transportDetachFailed;
  const list = Array.isArray(labels) ? labels.filter(Boolean).join(', ') : '';
  return list ? String(t('common:transportDetachFailed', { numbers: list })) : null;
}
